// src/pages/builder/PromoSettings.js
//
// Layout Builder → Rewards & avatar settings → 📣 Educator promotions.
//
// Two popups an admin can switch on for educators (shown on the educator
// dashboard, once a day each, until the end date):
//
//   coinDeal     "X% off coin packs" — the server takes X% off every coin pack
//                an educator buys while it's on (functions/coinStore.js)
//   productCode  a code like TEACH50 for X% off products in the store — the
//                server applies it at checkout when it's typed in the cart
//                (functions/cartCheckout.js)
//
// Saved to rewardSettings/main.promos.

import React, { useEffect, useState } from "react";
import { promoLive, savePromos, useRewardSettings } from "../../services/rewardSettingsService";

const BLANK = {
  coinDeal: { on: false, percent: 25, title: "Coins are on sale for teachers!", message: "Fill up your class coin bank for less — every coin pack is discounted for a limited time.", until: "" },
  productCode: { on: false, code: "TEACH50", percent: 50, title: "50% off in our store", message: "Use this code at checkout for half off classroom products.", until: "" },
};

const PromoSettings = () => {
  const { settings, loaded } = useRewardSettings();
  const [draft, setDraft] = useState(null);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loaded && draft === null) {
      const p = settings.promos || {};
      setDraft({ coinDeal: { ...BLANK.coinDeal, ...(p.coinDeal || {}) }, productCode: { ...BLANK.productCode, ...(p.productCode || {}) } });
    }
  }, [loaded, settings, draft]);

  if (!draft) return <p className="lb-hint">Loading…</p>;

  const set = (which, patch) => {
    setMsg(null);
    setDraft((d) => ({ ...d, [which]: { ...d[which], ...patch } }));
  };
  const clean = (d) => ({
    coinDeal: { ...d.coinDeal, percent: Math.max(1, Math.min(90, Math.floor(Number(d.coinDeal.percent) || 0))) },
    productCode: {
      ...d.productCode,
      code: String(d.productCode.code || "").toUpperCase().replace(/[^A-Z0-9-]/g, "").slice(0, 24),
      percent: Math.max(1, Math.min(90, Math.floor(Number(d.productCode.percent) || 0))),
    },
  });
  const save = async () => {
    const out = clean(draft);
    if (out.productCode.on && !out.productCode.code) {
      setMsg({ tone: "bad", text: "Type a code for the product discount (letters and numbers)." });
      return;
    }
    setBusy(true);
    try {
      await savePromos({ ...out, version: Date.now() });
      setDraft(out);
      setMsg({ tone: "ok", text: "Saved — educators see the popups on their next dashboard visit." });
    } catch (e) {
      setMsg({ tone: "bad", text: e?.message || "Couldn't save." });
    } finally {
      setBusy(false);
    }
  };

  const Block = ({ which, icon, heading, children }) => {
    const p = draft[which];
    const live = promoLive(clean(draft)[which]);
    return (
      <fieldset className="rws-group" style={{ display: "grid", gap: 10, padding: 14 }}>
        <legend>
          {icon} {heading}
        </legend>
        <label style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 800 }}>
          <input type="checkbox" checked={Boolean(p.on)} onChange={(e) => set(which, { on: e.target.checked })} />
          Show this popup to educators
          <span className={`shp-msg is-${live ? "ok" : "warn"}`} style={{ marginLeft: 8 }}>
            {live ? "LIVE NOW" : p.on ? "ended (check the end date)" : "off"}
          </span>
        </label>
        {children}
        <label>
          Popup title
          <input type="text" value={p.title} maxLength={80} onChange={(e) => set(which, { title: e.target.value })} style={{ width: "100%" }} />
        </label>
        <label>
          Popup message
          <textarea value={p.message} maxLength={240} rows={2} onChange={(e) => set(which, { message: e.target.value })} style={{ width: "100%" }} />
        </label>
        <label>
          Ends after (optional){" "}
          <input type="date" value={p.until || ""} onChange={(e) => set(which, { until: e.target.value })} />
          {p.until && (
            <button type="button" className="lb-btn lb-btn-soft" onClick={() => set(which, { until: "" })} style={{ marginLeft: 8 }}>
              No end date
            </button>
          )}
        </label>
      </fieldset>
    );
  };

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <p className="lb-hint">
        Popups for <b>educators</b> on their dashboard (once a day each, until you switch them off or the end date passes). The discounts are applied by the server,
        so they can't be faked.
      </p>

      {Block({
        which: "coinDeal",
        icon: "🪙",
        heading: "Coin pack discount",
        children: (
          <label>
            Discount{" "}
            <input type="number" min="1" max="90" value={draft.coinDeal.percent} onChange={(e) => set("coinDeal", { percent: e.target.value })} style={{ width: 70 }} /> % off every coin
            pack an educator buys
          </label>
        ),
      })}

      {Block({
        which: "productCode",
        icon: "🏷️",
        heading: "Product discount code",
        children: (
          <>
            <label>
              Code{" "}
              <input type="text" value={draft.productCode.code} onChange={(e) => set("productCode", { code: e.target.value.toUpperCase() })} style={{ width: 160, textTransform: "uppercase" }} />
            </label>
            <label>
              Discount{" "}
              <input type="number" min="1" max="90" value={draft.productCode.percent} onChange={(e) => set("productCode", { percent: e.target.value })} style={{ width: 70 }} /> % off
              products in the store
            </label>
          </>
        ),
      })}

      <div className="rws-save">
        {msg && <span className={`shp-msg is-${msg.tone}`}>{msg.text}</span>}
        <button type="button" className="lb-btn" onClick={save} disabled={busy}>
          {busy ? "Saving…" : "Save promotions"}
        </button>
      </div>
    </div>
  );
};

export default PromoSettings;
